import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Loader2, Check, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export type SavedMethod = {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
};

export type PaymentMethodPanelHandle = {
  startAdd: () => Promise<void>;
};

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "";

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!PUBLISHABLE_KEY) return null;
  stripePromise ??= loadStripe(PUBLISHABLE_KEY);
  return stripePromise;
}

function brandLabel(brand: string | null): string {
  if (!brand) return "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

export const PaymentMethodPanel = forwardRef<
  PaymentMethodPanelHandle,
  { onMethodChange?: (m: SavedMethod | null) => void }
>(function PaymentMethodPanel({ onMethodChange }, ref) {
  const [method, setMethod] = useState<SavedMethod | null | undefined>(
    undefined
  );
  const [adding, setAdding] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Stripe.js is lazy-loaded — only injected when the user actually clicks
  // "Add card", not on every dashboard mount.
  const [stripe, setStripeP] = useState<Promise<Stripe | null> | null>(null);

  async function load() {
    const res = await api<{ paymentMethod: SavedMethod | null }>(
      "/customer/payment-method"
    );
    const next = res.ok ? (res.data?.paymentMethod ?? null) : null;
    setMethod(next);
    onMethodChange?.(next);
  }

  useEffect(() => {
    load();
  }, []);

  useImperativeHandle(ref, () => ({ startAdd }), []);

  async function startAdd() {
    setError(null);
    if (!stripe) {
      const promise = getStripe();
      if (!promise) {
        setError("Stripe is not configured for this environment.");
        return;
      }
      setStripeP(promise);
    }
    const res = await api<{ clientSecret: string }>(
      "/customer/payment-method/setup-intent",
      { method: "POST" }
    );
    if (!res.ok || !res.data?.clientSecret) {
      setError(res.error ?? "Could not start the card-add flow.");
      return;
    }
    setClientSecret(res.data.clientSecret);
    setAdding(true);
  }

  async function removeCard() {
    if (!window.confirm("Remove the saved card?")) return;
    const res = await api("/customer/payment-method", { method: "DELETE" });
    if (res.ok) await load();
    else alert(res.error ?? "Could not remove the card.");
  }

  if (!PUBLISHABLE_KEY) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
        Stripe is not configured for this environment. The site administrator
        needs to set <code>VITE_STRIPE_PUBLISHABLE_KEY</code>.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
        <CreditCard className="h-4 w-4 text-slate-500" />
        Payment method
      </div>

      {method === undefined && (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {method === null && !adding && (
        <>
          <p className="mt-2 text-sm text-slate-600">
            No card on file. Add one for subscription billing.
          </p>
          <Button className="mt-4" onClick={startAdd}>
            Add a payment method
          </Button>
          {error && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </>
      )}

      {method && !adding && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-700">
            <Check className="mr-1 inline h-4 w-4 text-emerald-600" />
            {brandLabel(method.brand)} ending in{" "}
            <span className="font-mono">{method.last4}</span>
            {method.expMonth && method.expYear && (
              <span className="ml-2 text-xs text-slate-500">
                exp {String(method.expMonth).padStart(2, "0")}/
                {String(method.expYear).slice(2)}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={startAdd}>
              Replace
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={removeCard}
              className="text-destructive hover:text-destructive"
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {adding && clientSecret && stripe && (
        <Elements
          stripe={stripe}
          options={{ clientSecret, appearance: { theme: "stripe" } }}
        >
          <AddCardForm
            onSuccess={async () => {
              setAdding(false);
              setClientSecret(null);
              await load();
            }}
            onCancel={() => {
              setAdding(false);
              setClientSecret(null);
            }}
          />
        </Elements>
      )}
    </div>
  );
});

function AddCardForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => Promise<void>;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message ?? "Card details rejected.");
      setSubmitting(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard`,
      },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message ?? "Could not save the card.");
      setSubmitting(false);
      return;
    }

    const finalize = await api("/customer/payment-method/confirm", {
      method: "POST",
    });
    setSubmitting(false);
    if (!finalize.ok) {
      setError(finalize.error ?? "Card saved but could not set as default.");
      return;
    }
    await onSuccess();
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-4">
      <PaymentElement />
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting || !stripe || !elements}>
          {submitting && <Loader2 className="animate-spin" />}
          {submitting ? "Saving…" : "Save card"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
